import { useCallback, useEffect, useRef, useState } from "react";
import { useAppStore } from "../../store";
import { useDuelsStore } from "./store";
import type { Article } from "../wikidaily/types";
import i18n from "../../i18n/config";
import { getGameplayToken } from "../../auth";
import {
  fetchWikipediaArticleHtml,
  getRequestedGameLanguage,
} from "../../wikipediaApi";

type FrontendAction = { action: string; payload?: unknown };

function toZigMessage(message: FrontendAction): unknown {
  if (message.action === "change_accept") {
    return { to: "game", op: "change_accept", data: Boolean(message.payload) };
  }

  if (message.action === "move") {
    const payload = message.payload as
      { id?: number; article?: number } | undefined;
    return {
      to: "game",
      op: "move",
      data: { article: payload?.id ?? payload?.article },
    };
  }

  if (message.action === "reset_try") {
    return { to: "game", op: "reset_try", data: null };
  }

  if (
    message.action === "request_finish" ||
    message.action === "ready_to_finish"
  ) {
    return { to: "game", op: "request_finish", data: Boolean(message.payload) };
  }

  if (message.action === "player_info") {
    const payload = message.payload as { id?: number } | undefined;
    return { to: "server", op: "player_info", data: { id: payload?.id } };
  }

  return message;
}

function getArticleFromLoadPayload(payload: unknown): Article | null {
  if (typeof payload !== "object" || payload === null) return null;

  const id =
    (payload as { id?: unknown }).id ??
    (payload as { article?: unknown }).article;
  if (typeof id !== "number") return null;

  const title =
    (payload as { title?: unknown }).title ??
    (payload as { name?: unknown }).name;

  return {
    id,
    title: typeof title === "string" ? title : "",
    description:
      typeof (payload as { description?: unknown }).description === "string"
        ? (payload as { description: string }).description
        : null,
  };
}

function getDefaultDuelsWsUrl() {
  if (import.meta.env.VITE_DUELS_WS_URL)
    return import.meta.env.VITE_DUELS_WS_URL;

  if (typeof window === "undefined") return "/ws/duels";

  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${window.location.host}/ws/duels`;
}

export function useDuelsSocket(url = getDefaultDuelsWsUrl()) {
  const uiLanguage = useAppStore((state) => state.lang);
  const activeGameLanguage = useDuelsStore(
    (state) => state.gameState?.language,
  );
  const matchmakingLanguage =
    activeGameLanguage || getRequestedGameLanguage(uiLanguage);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const connectRef = useRef<() => void>(() => {});

  const connect = useCallback(async () => {
    if (reconnectRef.current) {
      clearTimeout(reconnectRef.current);
      reconnectRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.close();
    }

    let token: string;
    try {
      token = await getGameplayToken();
    } catch {
      useDuelsStore.getState().setConnectionError(i18n.t("errors.authFailed"));
      return;
    }

    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      useDuelsStore.getState().setConnectionError(null);
      ws.send(
        JSON.stringify({
          to: "server",
          op: "auth",
          data: {
            token,
            language: matchmakingLanguage,
          },
        }),
      );
    };

    ws.onmessage = async (event) => {
      try {
        const data = JSON.parse(event.data);
        if (typeof data?.action !== "string") return;

        const store = useDuelsStore.getState();

        if (data.action === "article_loaded") {
          const payload = data.payload as { article?: Article } | undefined;
          const article = payload?.article;
          if (!article) return;
          const gameLanguage = store.gameState?.language;
          if (!gameLanguage) {
            store.setConnectionError(i18n.t("errors.articleFetchFailed"));
            return;
          }
          const html = await fetchWikipediaArticleHtml(
            article.id,
            gameLanguage,
          );
          store.article_loaded({ article, html });
          return;
        }

        const method = store[data.action as keyof typeof store];
        if (typeof method === "function") {
          (method as (payload: unknown) => void)(data.payload);
        }
      } catch (error) {
        console.error("failed to handle duels message", error);
        useDuelsStore
          .getState()
          .setConnectionError(
            error instanceof Error
              ? error.message
              : i18n.t("errors.messageHandlingFailed"),
          );
      }
    };

    ws.onerror = () => {
      useDuelsStore
        .getState()
        .setConnectionError(i18n.t("errors.connectionFailed"));
    };

    ws.onclose = () => {
      setConnected(false);
      if (ws === wsRef.current) {
        reconnectRef.current = setTimeout(() => connectRef.current(), 1000);
      }
    };
  }, [matchmakingLanguage, url]);

  useEffect(() => {
    connectRef.current = connect;
    connectRef.current();

    return () => {
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect]);

  const send = useCallback((message: FrontendAction) => {
    if (
      message.action === "preload_move" ||
      message.action === "load_article"
    ) {
      const article = getArticleFromLoadPayload(message.payload);
      if (!article) {
        useDuelsStore
          .getState()
          .setConnectionError(i18n.t("errors.missingArticleId"));
        return false;
      }

      const gameLanguage = useDuelsStore.getState().gameState?.language;
      if (!gameLanguage) {
        useDuelsStore
          .getState()
          .setConnectionError(i18n.t("errors.articleFetchFailed"));
        return false;
      }

      void fetchWikipediaArticleHtml(article.id, gameLanguage)
        .then((html) => {
          useDuelsStore.getState().article_loaded({ article, html });
          useDuelsStore.getState().setConnectionError(null);
        })
        .catch((error) => {
          console.error("failed to fetch article", error);
          useDuelsStore
            .getState()
            .setConnectionError(
              error instanceof Error
                ? error.message
                : i18n.t("errors.articleFetchFailed"),
            );
        });

      return true;
    }

    if (wsRef.current?.readyState !== WebSocket.OPEN) {
      useDuelsStore
        .getState()
        .setConnectionError(i18n.t("errors.websocketClosedShort"));
      return false;
    }

    wsRef.current.send(JSON.stringify(toZigMessage(message)));
    return true;
  }, []);

  return { connected, reconnect: connect, send };
}
